/**
 * Error surface for the Stoxly API client.
 *
 * HTTP errors are wrapped in `StoxlyApiError` by the ky runtime, so callers
 * always get an Error instance carrying the HTTP status and parsed body.
 * Zod response-validation failures surface as `StoxlyValidationError`.
 * Transport failures pass through as ky errors (TimeoutError/NetworkError)
 * or AbortError.
 */
import type { ZodError } from 'zod';

/** NestJS error bodies are `{ statusCode, message: string | string[], error }`. */
function extractMessage(body: unknown): string | undefined {
  if (typeof body === 'string' && body.length > 0) {
    return body;
  }
  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as { message: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
    if (Array.isArray(message)) {
      return message.join(', ');
    }
  }
  return undefined;
}

export class StoxlyApiError extends Error {
  readonly status: number;
  /** Parsed response body (NestJS error shape when the server sent JSON). */
  readonly body: unknown;
  /** Parsed `retry-after` response header (seconds), when the server sent one. */
  readonly retryAfterSeconds?: number;

  constructor(status: number, body: unknown, retryAfterHeader?: string | null) {
    super(extractMessage(body) ?? `Request failed with status ${status}`);
    this.name = 'StoxlyApiError';
    this.status = status;
    this.body = body;
    const parsed = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : Number.NaN;
    if (!Number.isNaN(parsed)) {
      this.retryAfterSeconds = parsed;
    }
  }
}

export function isStoxlyApiError(error: unknown): error is StoxlyApiError {
  return error instanceof StoxlyApiError;
}

/** Thrown when a JSON response does not match the endpoint's generated Zod schema. */
export class StoxlyValidationError extends Error {
  /** The offending parsed response payload. */
  readonly data: unknown;
  override readonly cause: ZodError;

  constructor(endpoint: string, data: unknown, cause: ZodError) {
    super(`Invalid response from endpoint '${endpoint}'`);
    this.name = 'StoxlyValidationError';
    this.data = data;
    this.cause = cause;
  }
}

export function isStoxlyValidationError(error: unknown): error is StoxlyValidationError {
  return error instanceof StoxlyValidationError;
}

export interface ClassifiedError {
  message: string;
  code: string;
  /** Worth retrying (rate limit, server hiccup, network) vs a caller bug or auth failure. */
  recoverable: boolean;
  status?: number;
  /** Seconds the server asked to wait before retrying (429/503). */
  retryAfterSeconds?: number;
}

function classifyHttpError(error: StoxlyApiError): ClassifiedError {
  const { status, retryAfterSeconds } = error;
  switch (status) {
    case 400:
      return { message: error.message, code: 'BAD_REQUEST', recoverable: false, status };
    case 401:
      return {
        message: 'Authentication required',
        code: 'UNAUTHORIZED',
        recoverable: false,
        status,
      };
    case 403:
      return { message: 'Access denied', code: 'FORBIDDEN', recoverable: false, status };
    case 404:
      return { message: error.message, code: 'NOT_FOUND', recoverable: false, status };
    case 429:
      return {
        message: 'Rate limit exceeded. Please try again later.',
        code: 'RATE_LIMIT',
        recoverable: true,
        status,
        retryAfterSeconds: retryAfterSeconds ?? 60,
      };
    case 500:
      return {
        message: 'Server error. Please try again.',
        code: 'INTERNAL_SERVER_ERROR',
        recoverable: true,
        status,
      };
    case 502:
      return {
        message: 'Service temporarily unavailable',
        code: 'BAD_GATEWAY',
        recoverable: true,
        status,
      };
    case 503:
      return {
        message: 'Service unavailable. Please try again later.',
        code: 'SERVICE_UNAVAILABLE',
        recoverable: true,
        status,
        retryAfterSeconds: retryAfterSeconds ?? 30,
      };
    case 504:
      return {
        message: 'Service temporarily unavailable. Please try again.',
        code: 'GATEWAY_TIMEOUT',
        recoverable: true,
        status,
        retryAfterSeconds: retryAfterSeconds ?? 30,
      };
    default:
      return {
        message: error.message,
        code: `SERVER_ERROR_${status}`,
        recoverable: status >= 500,
        status,
      };
  }
}

export function classifyError(error: unknown): ClassifiedError {
  if (isStoxlyApiError(error)) {
    return classifyHttpError(error);
  }
  if (isStoxlyValidationError(error)) {
    return {
      message: 'The server completed the request, but its reply could not be read.',
      code: 'INVALID_RESPONSE',
      recoverable: false,
    };
  }
  if (error instanceof Error) {
    if (error.name === 'TimeoutError') {
      return { message: 'Request timeout. Please try again.', code: 'TIMEOUT', recoverable: true };
    }
    if (error.name === 'AbortError') {
      return { message: 'Request cancelled', code: 'REQUEST_CANCELLED', recoverable: false };
    }
    // ky v2 wraps network failures in NetworkError; bare fetch (custom `fetch`
    // overrides on unrecognized runtimes) surfaces them as TypeError
    if (error.name === 'NetworkError' || error instanceof TypeError) {
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        recoverable: true,
      };
    }
    return { message: error.message, code: 'CLIENT_ERROR', recoverable: false };
  }
  return { message: 'An unexpected error occurred', code: 'CLIENT_ERROR', recoverable: false };
}

export function isRecoverableError(error: unknown): boolean {
  return classifyError(error).recoverable;
}
